import type { AuthUser } from '@pms/shared';

import { db, getMeta, setMeta } from '../sync/db.js';
import { META_PULL_CURSOR } from '../sync/types.js';

/**
 * Сессия на устройстве.
 *
 * Токен и сведения о вошедшем хранятся в той же локальной базе, что и очередь, —
 * иначе после перезагрузки телефона оператор оказался бы на экране входа
 * с непонятно чьей очередью неотправленных отчётов.
 */

const META_TOKEN = 'authToken';
const META_TOKEN_EXPIRES = 'authTokenExpiresAt';
const META_USER = 'authUser';

/**
 * Копия сессии для приложения найма.
 *
 * Оно живёт на том же домене по адресу /hr/, но это отдельное приложение со
 * своим хранилищем. Без этой копии человек, уже вошедший в систему, при
 * переходе на найм вводил бы логин и пароль второй раз подряд — ради того же
 * самого токена.
 *
 * Лежит в localStorage, потому что читать чужую IndexedDB — значит завязать
 * одно приложение на внутреннее устройство другого. Опасности это не добавляет:
 * оба хранилища одинаково доступны коду этого домена и никому больше.
 */
const SHARED_SESSION_KEY = 'pms.session';

function mirrorForHr(session: StoredSession): void {
  try {
    if (session.token && session.user) {
      localStorage.setItem(SHARED_SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(SHARED_SESSION_KEY);
    }
  } catch {
    // Приватный режим может запрещать localStorage. Цеховому приложению это
    // безразлично — оно хранит сессию в своей базе; найм просто спросит вход.
  }
}

export interface StoredSession {
  token: string | null;
  expiresAt: string | null;
  user: AuthUser | null;
}

export async function loadSession(): Promise<StoredSession> {
  const [token, expiresAt, user] = await Promise.all([
    getMeta<string>(META_TOKEN),
    getMeta<string>(META_TOKEN_EXPIRES),
    getMeta<AuthUser>(META_USER),
  ]);
  const session = { token: token ?? null, expiresAt: expiresAt ?? null, user: user ?? null };
  // Обновляем копию при каждом запуске: у тех, кто вошёл до появления общего
  // входа, копии ещё нет, и заставлять их логиниться заново незачем.
  mirrorForHr(session);
  return session;
}

export async function saveSession(session: {
  token: string;
  expiresAt: string;
  user: AuthUser;
}): Promise<void> {
  await db.transaction('rw', db.meta, async () => {
    await db.meta.put({ key: META_TOKEN, value: session.token });
    await db.meta.put({ key: META_TOKEN_EXPIRES, value: session.expiresAt });
    await db.meta.put({ key: META_USER, value: session.user });
  });
  mirrorForHr(session);
}

/** Вход без сети: кто работает, знаем, а токена ещё нет — он появится при связи. */
export async function saveLocalUser(user: AuthUser): Promise<void> {
  await setMeta(META_USER, user);
}

/**
 * Выход. Токен убираем, очередь и справочники — нет:
 * неотправленные отчёты не принадлежат сессии, они принадлежат смене.
 */
export async function clearSession(): Promise<void> {
  await db.transaction('rw', db.meta, async () => {
    await db.meta.delete(META_TOKEN);
    await db.meta.delete(META_TOKEN_EXPIRES);
    await db.meta.delete(META_USER);
  });
  mirrorForHr({ token: null, expiresAt: null, user: null });
}

/**
 * Смена человека на устройстве: убрать чужое личное.
 *
 * Один планшет в цеху проходит через руки нескольких человек. Уведомления
 * приходят каждому свои, но лежат в общей базе устройства, а курсор загрузки
 * на всё устройство один: без этой чистки следующий вошедший видел бы чужие
 * уведомления и не получил бы своих — их номера уже «пройдены» курсором.
 *
 * Очередь отправки НЕ трогаем: несданные отчёты должны уйти на сервер
 * независимо от того, кто сейчас держит планшет.
 */
export async function forgetPersonalData(): Promise<void> {
  await db.transaction('rw', db.notifications, db.meta, async () => {
    await db.notifications.clear();
    await db.meta.delete(META_PULL_CURSOR);
  });
}

/** Только токен — при истечении срока, чтобы не терять, кто работал на устройстве. */
export async function clearToken(): Promise<void> {
  await db.transaction('rw', db.meta, async () => {
    await db.meta.delete(META_TOKEN);
    await db.meta.delete(META_TOKEN_EXPIRES);
  });
  // Токена больше нет — найму его отдавать нечего
  mirrorForHr({ token: null, expiresAt: null, user: null });
}

export async function currentToken(): Promise<string | null> {
  return (await getMeta<string>(META_TOKEN)) ?? null;
}

export async function authHeaders(): Promise<Record<string, string>> {
  const token = await currentToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
